import { useEffect, useState } from "react";

import { api, type PublicDirectoryTournament } from "../../lib/api";
import { SPORT_LABELS } from "../../lib/labels";
import { matchesDirectoryQuery } from "../../lib/publicDirectory";

export type DirectoryStatus = "loading" | "ready" | "error";

/** Torneos públicos con inscripciones abiertas (Eduardo 2026-09-26). El
 * backend ya descarta los torneos empezados o con todas sus categorías llenas. */
export function useTournamentDirectory(): { tournaments: PublicDirectoryTournament[]; status: DirectoryStatus } {
  const [tournaments, setTournaments] = useState<PublicDirectoryTournament[]>([]);
  const [status, setStatus] = useState<DirectoryStatus>("loading");
  useEffect(() => {
    let active = true;
    api.publicTournaments
      .directory()
      .then((response) => {
        if (!active) return;
        setTournaments(response.data.tournaments);
        setStatus("ready");
      })
      .catch(() => {
        if (active) setStatus("error");
      });
    return () => {
      active = false;
    };
  }, []);
  return { tournaments, status };
}

/** Deportes que siempre se ofrecen en el filtro (las mismas claves que
 * backend/app/modules/sports). */
export const DIRECTORY_SPORTS: string[] = Object.keys(SPORT_LABELS);

/** Parámetros de la URL de /torneos: ?q= (texto) y ?deporte= (clave). */
export const QUERY_PARAM = "q";
export const SPORT_PARAM = "deporte";

/** Torneos que pasan el texto y el deporte elegidos ("" = todos). */
export function filterDirectory<T extends PublicDirectoryTournament>(tournaments: T[], query: string, sport: string): T[] {
  return tournaments.filter(
    (tournament) => (!sport || (tournament.sport || "tenis") === sport) && matchesDirectoryQuery(tournament, query),
  );
}

/** Ruta del buscador del inicio: "/torneos?q=copa&deporte=padel" (sin los
 * parámetros vacíos). */
export function directorySearchPath(query: string, sport: string): string {
  const params = new URLSearchParams();
  const text = query.trim();
  if (text) params.set(QUERY_PARAM, text);
  if (sport) params.set(SPORT_PARAM, sport);
  const search = params.toString();
  return search ? `/torneos?${search}` : "/torneos";
}

/** Rejilla de «Próximos torneos» del inicio: máximo 4 tarjetas. */
export const HOME_GRID_SIZE = 4;

/**
 * Cuántas columnas ocupa la tarjeta de promoción para que la rejilla no deje
 * huecos: `wide` con 4 columnas (≥1181 px) y `medium` con 2 (861–1180 px). En
 * una columna siempre ocupa el renglón completo. null = no hace falta (hay 4).
 */
export function promoSpans(count: number): { wide: number; medium: number } | null {
  const shown = Math.max(0, Math.min(count, HOME_GRID_SIZE));
  if (shown >= HOME_GRID_SIZE) return null;
  return { wide: HOME_GRID_SIZE - shown, medium: shown % 2 === 1 ? 1 : 2 };
}
